import type { Wallpaper } from '@/types/wallpaper';

export interface SEOMetaTags {
  title: string;
  description: string;
  keywords: string;
  ogImage?: string;
  ogType?: string;
  robots?: string;
  canonical?: string;
}

export class SEOManager {
  private static instance: SEOManager;

  static getInstance(): SEOManager {
    if (!SEOManager.instance) {
      SEOManager.instance = new SEOManager();
    }
    return SEOManager.instance;
  }

  // Generate dynamic meta tags for wallpaper detail pages
  generateWallpaperMeta(wallpaper: Wallpaper): SEOMetaTags {
    const title = `${wallpaper.title} - Free HD Wallpaper Download | WallpaperHub`;
    const description = `Download "${wallpaper.title}" wallpaper in HD and 4K quality. ${wallpaper.description || 'Perfect for desktop, mobile, and tablet backgrounds.'} Free for personal and commercial use.`;
    const keywords = [
      wallpaper.title.toLowerCase(),
      wallpaper.category.toLowerCase() + ' wallpaper',
      'HD wallpaper download',
      '4K wallpaper',
      'free wallpaper',
      ...(wallpaper.tags || [])
    ].join(', ');

    return {
      title,
      description,
      keywords,
      ogImage: wallpaper.thumbnailUrl || wallpaper.imageUrl,
      ogType: 'article',
      robots: 'index, follow'
    };
  }

  // Generate dynamic meta tags for category pages
  generateCategoryMeta(category: string, wallpaperCount?: number): SEOMetaTags {
    const categoryTitle = category.charAt(0).toUpperCase() + category.slice(1);
    const title = `${categoryTitle} Wallpapers - Free HD Downloads | WallpaperHub`;
    const description = `Browse and download ${wallpaperCount ? `${wallpaperCount}+ ` : ''}free ${categoryTitle.toLowerCase()} wallpapers in HD and 4K quality. Perfect for desktop, mobile, and tablet backgrounds.`;
    const keywords = `${categoryTitle.toLowerCase()} wallpapers, ${categoryTitle.toLowerCase()} backgrounds, HD ${categoryTitle.toLowerCase()} wallpapers, 4K ${categoryTitle.toLowerCase()} wallpapers, free ${categoryTitle.toLowerCase()} wallpapers download`;

    return {
      title,
      description,
      keywords,
      ogType: 'website',
      robots: 'index, follow'
    };
  }

  // Generate dynamic meta tags for search results
  generateSearchMeta(query: string, resultCount?: number): SEOMetaTags {
    const title = `"${query}" Wallpapers - Search Results | WallpaperHub`;
    const description = `${resultCount ? `Found ${resultCount} ` : 'Search '}wallpapers matching "${query}". Download free HD and 4K wallpapers for desktop, mobile, and tablet.`;
    const keywords = `${query} wallpapers, ${query} backgrounds, search ${query}, HD ${query} wallpapers, free ${query} wallpapers`;

    return {
      title,
      description,
      keywords,
      ogType: 'website',
      robots: 'index, follow'
    };
  }

  // Apply meta tags to the document
  applyMetaTags(metaTags: SEOMetaTags): void {
    // Update title
    document.title = metaTags.title;

    // Update or create meta tags
    this.updateMetaTag('name', 'description', metaTags.description);
    this.updateMetaTag('name', 'keywords', metaTags.keywords);
    this.updateMetaTag('name', 'robots', metaTags.robots || 'index, follow');

    // Update Open Graph tags
    this.updateMetaTag('property', 'og:title', metaTags.title);
    this.updateMetaTag('property', 'og:description', metaTags.description);
    this.updateMetaTag('property', 'og:type', metaTags.ogType || 'website');
    this.updateMetaTag('property', 'og:url', window.location.href);

    if (metaTags.ogImage) {
      this.updateMetaTag('property', 'og:image', metaTags.ogImage);
      this.updateMetaTag('property', 'og:image:alt', metaTags.title);
    }

    // Update Twitter Card tags
    this.updateMetaTag('name', 'twitter:card', 'summary_large_image');
    this.updateMetaTag('name', 'twitter:title', metaTags.title);
    this.updateMetaTag('name', 'twitter:description', metaTags.description);
    if (metaTags.ogImage) {
      this.updateMetaTag('name', 'twitter:image', metaTags.ogImage);
    }

    // Update canonical URL
    this.updateCanonicalUrl(metaTags.canonical || window.location.href);
  }

  private updateMetaTag(attribute: string, name: string, content: string): void {
    let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attribute, name);
      document.head.appendChild(element);
    }
    element.setAttribute('content', content);
  }

  private updateCanonicalUrl(url: string): void {
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);
  }

  // Generate JSON-LD structured data for wallpapers
  generateWallpaperStructuredData(wallpaper: Wallpaper): string {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "ImageObject",
      "name": wallpaper.title,
      "description": wallpaper.description || `Beautiful ${wallpaper.category} wallpaper`,
      "contentUrl": wallpaper.imageUrl,
      "thumbnailUrl": wallpaper.thumbnailUrl,
      "width": (wallpaper as any).width,
      "height": (wallpaper as any).height,
      "encodingFormat": "image/jpeg",
      "keywords": wallpaper.tags?.join(', ') || wallpaper.category,
      "dateCreated": wallpaper.createdAt,
      "creator": {
        "@type": "Organization",
        "name": "WallpaperHub"
      },
      "license": "https://creativecommons.org/publicdomain/zero/1.0/",
      "acquireLicensePage": `${window.location.origin}/wallpaper/${wallpaper.id}`,
      "creditText": "WallpaperHub",
      "copyrightNotice": "Free for personal and commercial use"
    };

    return JSON.stringify(structuredData);
  }

  // Generate JSON-LD structured data for the website
  generateWebsiteStructuredData(): string {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "WallpaperHub",
      "description": "Download thousands of free HD wallpapers and backgrounds for desktop, mobile, and tablet",
      "url": window.location.origin,
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${window.location.origin}/search?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      },
      "sameAs": [
        // Add your social media URLs here
        // "https://twitter.com/wallpaperhub",
        // "https://facebook.com/wallpaperhub"
      ]
    };

    return JSON.stringify(structuredData);
  }

  // Inject structured data into the page
  injectStructuredData(structuredData: string, id = 'structured-data'): void {
    // Remove existing structured data with the same ID
    const existing = document.getElementById(id);
    if (existing) {
      existing.remove();
    }

    // Create and inject new structured data
    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = structuredData;
    document.head.appendChild(script);
  }
}

// Export singleton instance
export const seoManager = SEOManager.getInstance();